import type { ISubTitleProps } from './elements/Subtitle';
import type { Control, UseFormReturn } from 'react-hook-form';

import React, { useContext } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useMutation } from 'react-query';

import Subtitle from './elements/Subtitle';
import Title from './elements/Title';

import ApiClient from '$/ApiClient';
import StudentQuestion from '$/components/blocks/StudentQuestion';
import FileInput from '$/components/elements/Inputs/FileInput';
import getControlError from '$/hooks/getControlError';
import { CurrentUserContext } from '$/providers/CurrentUserProvider';
import handleCommonToast from '$/utils/handleCommonToast';

interface IFileInputQuestion {
  control: UseFormReturn<any>['control'];
  name: string;
  title?: string;
  subtitle?: string;
  subtitleProps?: ISubTitleProps;
  max: number;
  onCancel?: () => void;
  additionalDocumentProps?: Record<string, string | number>;
  errorMessage?: string;
}

const ResponsiveFileInputQuestion = (props: IFileInputQuestion) => {
  const { fields, append, remove } = useFieldArray({
    control: props.control, // control props comes from useForm (optional: if you are using FormContext)
    name: props.name, // unique name for your Field Array
    // keyName: "id", default to "id", you can change the key name
  });

  const { userQuery } = useContext(CurrentUserContext);

  const createDocumentMutation = useMutation(ApiClient.createDocument, {
    onError: (error: any) =>
      handleCommonToast('error', error?.code ?? error, {}, error),
  });

  const removeDocumentMutation = useMutation(ApiClient.removeDocument, {
    onError: (error: any) =>
      handleCommonToast('error', error?.code ?? error, {}, error),
  });

  return (
    <>
      <div style={{ margin: '18px 8px 8px' }}>
        {props.title && <Title style={{ margin: 0 }}>{props.title}</Title>}
        {props.subtitle && (
          <Subtitle style={{ margin: 0 }}>{props.subtitle}</Subtitle>
        )}
      </div>
      <StudentQuestion.Question>
        {fields.map((element, index) => {
          return (
            <Controller
              key={element.id} // important to include key with field's id
              control={props.control}
              name={`${props.name}[${index}].file`}
              defaultValue={element?.file}
              render={({ field, fieldState }) => {
                const error = getControlError(
                  props.control,
                  props.name,
                  props.errorMessage
                );

                return (
                  <FileInput
                    {...field}
                    additionalDocumentProps={props.additionalDocumentProps}
                    onCancel={async () => {
                      remove(index);

                      if (!field.value) return;

                      await removeDocumentMutation.mutateAsync({
                        documentId: field.value.id,
                      });

                      await userQuery?.refetch();
                    }}
                    error={fieldState.invalid}
                    errorMessage={error.message}
                  />
                );
              }}
            />
          );
        })}
        {fields.length < props.max && (
          <FileInput
            key={fields.length}
            additionalDocumentProps={props.additionalDocumentProps}
            onSuccess={async (document) => {
              const response = await createDocumentMutation.mutateAsync({
                document: {
                  ...document,
                  field: props.name,
                },
              });

              append({
                // id: response.createDocument.id,
                file: {
                  ...document,
                  id: response.createDocument?.id,
                  field: props.name,
                },
              });

              await userQuery?.refetch();
            }}
            name={props.name}
            value={null}
          />
        )}
      </StudentQuestion.Question>
    </>
  );
};

export default ResponsiveFileInputQuestion;
